import type { ResultOf, VariablesOf } from '@/utilities/gql';
import { gql } from '@/utilities/gql';
import {
  infiniteQueryOptions,
  useSuspenseInfiniteQuery,
} from '@tanstack/react-query';
import { NARRATIVE_CARD_FRAGMENT } from '@/components/NarrativeCard';
import { gqlClient } from '@/utilities/graphQLClient';
import { getOperationName } from '@/utilities/gqlQuery/query';
import { NARRATIVE_COMMUNITY_LIST_DOCUMENT_HEADER_FRAGMENT } from '../components/NarrativeCommunityListDocumentHeader';
import {
  narrativeListPageParamsSafeParse,
  narrativeListRemoveNilFields,
} from '../utils';
import type { QueryTag } from '@/utilities/gqlQuery/types';

const QUERY = gql(
  `
  query NarrativesCommunityInfinite($first: Int, $last: Int, $before: String, $after: String, $filter: NarrativeFilterInput, $sort: NarrativeSortInput) {
    narratives(first: $first, last: $last, before: $before, after: $after, filter: $filter, sort: $sort) {
      edges {
        cursor
        node {
          id
          ...NarrativeCardFields
          ...NarrativeCommunityListDocumentHeaderFields
        }
      }
      pageInfo {
        startCursor
        endCursor
        hasNextPage
        hasPreviousPage
      }
    }
  }
`,
  [NARRATIVE_CARD_FRAGMENT, NARRATIVE_COMMUNITY_LIST_DOCUMENT_HEADER_FRAGMENT]
);

export type Variables = VariablesOf<typeof QUERY>;
export type Result = ResultOf<typeof QUERY>;
export type Narrative = Result['narratives']['edges'][number]['node'];
const operationName = getOperationName(QUERY)!;

export const useNarrativesCommunityInfinite = (variables: Variables) => {
  return useSuspenseInfiniteQuery({
    ...useNarrativesCommunityInfinite.getOptions(variables),
    staleTime: 0,
  });
};

useNarrativesCommunityInfinite.getOptions = (
  variables: VariablesOf<typeof QUERY>
) =>
  infiniteQueryOptions({
    // eslint-disable-next-line @tanstack/query/exhaustive-deps
    queryKey: [...operationName, variables.filter, variables.sort],
    initialPageParam: narrativeListPageParamsSafeParse({
      after: variables.after,
      before: variables.before,
    }),
    queryFn: ({ pageParam, direction }) => {
      const targetPageParam = narrativeListPageParamsSafeParse(pageParam);

      return gqlClient.request(
        QUERY,
        direction === 'backward' || targetPageParam.before
          ? {
              last: variables?.last,
              before: targetPageParam.before,
              filter: narrativeListRemoveNilFields(variables?.filter),
              sort: variables?.sort,
            }
          : {
              first: variables?.first,
              after: targetPageParam.after,
              filter: narrativeListRemoveNilFields(variables?.filter),
              sort: variables?.sort,
            }
      );
    },
    getNextPageParam: (lastPage: Result) => {
      if (!lastPage.narratives.pageInfo.hasNextPage) return null;
      return narrativeListPageParamsSafeParse({
        after: lastPage.narratives.pageInfo.endCursor,
        before: null,
      });
    },
    getPreviousPageParam: (firstPage: Result) => {
      if (!firstPage.narratives.pageInfo.hasPreviousPage) return null;
      return narrativeListPageParamsSafeParse({
        before: firstPage.narratives.pageInfo.startCursor,
        after: null,
      });
    },
    meta: {
      tags: ['narrative', 'narrative-community-list'] satisfies QueryTag[],
    },
  });

useNarrativesCommunityInfinite.getAllOptions = () =>
  infiniteQueryOptions({
    ...useNarrativesCommunityInfinite.getOptions({}),
    queryKey: operationName,
  });
